import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";

type DraftRecord<T> = { version: 1; updatedAt: number; generation?: string; value: T };
type GenerationMarker = { version: 1; generation: string; completedAt: number; pendingSubmissionID?: string };
type CompletionNotice = { key: string; generation: string; nextGeneration: string; completedAt: number };
type DraftStorageResult<T> = { generation: string; stale: boolean; pendingSubmissionID?: string; record?: DraftRecord<T> };
type DraftOperation = "read" | "check" | "put" | "claim" | "finish" | "mark";

const INITIAL_GENERATION = "initial";
const DRAFT_CHANNEL = "listwork:workspace-drafts";
const pendingWrites = new Map<string, Promise<unknown>>();
const completions = new Map<string, CompletionNotice>();
const completionKey = (key: string) => "listwork:draft-completed:" + key;
const generationKey = (key: string, generation: string) =>
  "listwork:draft-generation:" + encodeURIComponent(key) + ":" + encodeURIComponent(generation);
const lockKey = (key: string) => "listwork:draft:" + key;
const staleDraftMessage = "This draft was submitted or discarded in another tab. Close it to return to your workspace. Changes made here have not been saved.";

class StaleDraftError extends Error {
  constructor() { super(staleDraftMessage); }
}

function completionFor(key: string, generation: string) {
  const storageKey = generationKey(key, generation);
  const known = completions.get(storageKey);
  if (known) return known;
  try {
    const notice = JSON.parse(localStorage.getItem(storageKey) ?? "null") as CompletionNotice | null;
    if (notice?.key === key && notice.generation === generation && typeof notice.nextGeneration === "string"
      && notice.nextGeneration !== generation && Number.isFinite(notice.completedAt)) return notice;
  } catch { /* IndexedDB remains the primary generation authority. */ }
  return undefined;
}

function publishCompletion(notice: CompletionNotice) {
  completions.set(generationKey(notice.key, notice.generation), notice);
  try {
    localStorage.setItem(generationKey(notice.key, notice.generation), JSON.stringify(notice));
    localStorage.setItem(completionKey(notice.key), String(notice.completedAt));
  } catch { /* The caller reports incomplete durable cleanup. */ }
  window.dispatchEvent(new CustomEvent<CompletionNotice>(DRAFT_CHANNEL, { detail: notice }));
  try {
    const channel = new BroadcastChannel(DRAFT_CHANNEL);
    channel.postMessage(notice);
    channel.close();
  } catch { /* Every mutation also checks the durable generation. */ }
}

function openDraftDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("listwork-workspace-drafts", 1);
    let expired = false;
    const timer = window.setTimeout(() => {
      expired = true;
      reject(new Error("Draft storage did not respond."));
    }, 4000);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("drafts")) request.result.createObjectStore("drafts");
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      if (expired) request.result.close();
      else resolve(request.result);
    };
    request.onerror = () => { clearTimeout(timer); reject(request.error); };
    request.onblocked = () => { clearTimeout(timer); expired = true; reject(new Error("Draft storage is blocked.")); };
  });
}

// Generation checks and writes share one transaction. No async work belongs inside it.
async function draftOperation<T>(
  key: string,
  operation: DraftOperation,
  expectedGeneration?: string,
  value?: T,
  completion?: CompletionNotice,
  pendingSubmissionID?: string
): Promise<DraftStorageResult<T>> {
  const db = await openDraftDatabase();
  try {
    return await new Promise<DraftStorageResult<T>>((resolve, reject) => {
      const transaction = db.transaction("drafts", operation === "check" ? "readonly" : "readwrite");
      const store = transaction.objectStore("drafts");
      const request = store.get(completionKey(key));
      let result: DraftStorageResult<T> = { generation: INITIAL_GENERATION, stale: false };
      let failure: unknown;
      request.onsuccess = () => {
        try {
          const marker = request.result as GenerationMarker | undefined;
          if (marker && (marker.version !== 1 || typeof marker.generation !== "string" || !marker.generation
            || (marker.pendingSubmissionID !== undefined && (typeof marker.pendingSubmissionID !== "string" || !marker.pendingSubmissionID)))) {
            throw new Error("Draft generation could not be read.");
          }
          const generation = marker?.generation ?? INITIAL_GENERATION;
          result.generation = generation;
          result.pendingSubmissionID = marker?.pendingSubmissionID;
          if (expectedGeneration !== undefined && expectedGeneration !== generation) {
            result.stale = true;
            return;
          }
          const retired = completionFor(key, generation);
          if (retired) {
            if (operation !== "read") { result.stale = true; return; }
            // Heal a successful submission whose primary cleanup was unavailable.
            store.put({ version: 1, generation: retired.nextGeneration, completedAt: retired.completedAt }, completionKey(key));
            store.delete(key);
            result.generation = retired.nextGeneration;
            result.pendingSubmissionID = undefined;
            return;
          }
          if (marker?.pendingSubmissionID && operation !== "read"
            && !(["finish", "mark"].includes(operation) && marker.pendingSubmissionID === pendingSubmissionID)) {
            result.stale = true;
            return;
          }
          if (operation === "read") {
            const draftRequest = store.get(key);
            draftRequest.onsuccess = () => { result.record = draftRequest.result as DraftRecord<T> | undefined; };
          } else if (operation === "put") {
            store.put({ version: 1, generation, updatedAt: Date.now(), value }, key);
          } else if (operation === "claim") {
            if (!pendingSubmissionID) throw new Error("A submission identity is required.");
            // Commit the latest draft and its claim together, before sending inventory.
            // This remains authoritative if every post-success cleanup path fails.
            store.put({ version: 1, generation, completedAt: marker?.completedAt ?? 0, pendingSubmissionID }, completionKey(key));
            store.put({ version: 1, generation, updatedAt: Date.now(), value }, key);
            result.pendingSubmissionID = pendingSubmissionID;
          } else if (operation === "finish" || operation === "mark") {
            if (!completion || completion.generation !== generation) throw new Error("Draft completion is not current.");
            store.put({ version: 1, generation: completion.nextGeneration, completedAt: completion.completedAt }, completionKey(key));
            if (operation === "finish") store.delete(key);
            result.generation = completion.nextGeneration;
          }
        } catch (error) {
          failure = error;
          transaction.abort();
        }
      };
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(failure ?? transaction.error ?? new Error("Draft storage failed."));
      transaction.onabort = () => reject(failure ?? transaction.error ?? new Error("Draft storage was interrupted."));
    });
  } finally {
    db.close();
  }
}

function requireCurrent<T>(result: DraftStorageResult<T>) {
  if (result.stale) throw new StaleDraftError();
  return result;
}

function enqueue<T>(key: string, task: () => Promise<T>): Promise<T> {
  const next = (pendingWrites.get(key) ?? Promise.resolve()).catch(() => undefined).then(task);
  pendingWrites.set(key, next);
  void next.finally(() => { if (pendingWrites.get(key) === next) pendingWrites.delete(key); }).catch(() => undefined);
  return next;
}

function lockingAvailable() {
  return typeof navigator !== "undefined" && typeof navigator.locks?.request === "function";
}

async function withDraftLock<T>(key: string, task: () => Promise<T>, submission = false): Promise<T> {
  if (!lockingAvailable()) {
    if (submission) throw new Error("This browser cannot safely add this draft. Use a supported browser before adding the item.");
    return task();
  }
  if (submission) {
    return navigator.locks.request(lockKey(key), { mode: "exclusive", ifAvailable: true }, (lock) => {
      if (!lock) throw new Error("This draft is busy in another tab. Wait for that operation to finish.");
      return task();
    });
  }
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    return await navigator.locks.request(lockKey(key), { mode: "exclusive", signal: controller.signal }, () => {
      window.clearTimeout(timeout);
      return task();
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

async function completeDraft(key: string, generation: string, submitted: boolean, pendingSubmissionID?: string) {
  const notice: CompletionNotice = { key, generation, nextGeneration: crypto.randomUUID(), completedAt: Date.now() };
  let cleared = false;
  try {
    requireCurrent(await draftOperation(key, "finish", generation, undefined, notice, pendingSubmissionID));
    cleared = true;
  } catch (error) {
    if (error instanceof StaleDraftError) throw error;
    try {
      // Retire the generation even if removing the old photo record failed.
      requireCurrent(await draftOperation(key, "mark", generation, undefined, notice, pendingSubmissionID));
    } catch (markerError) {
      if (markerError instanceof StaleDraftError) throw markerError;
      if (!submitted) throw new Error("The draft could not be discarded safely. Keep it open and try again.");
      // An inventory success must not become a retryable save failure.
      // The per-generation mirror cannot retire or delete a newer editor's work.
    }
  }
  publishCompletion(notice);
  return cleared;
}

// Blob URLs belong to the current document. Store their bytes so photos survive a reload.
async function durableSnapshot<T>(value: T): Promise<T> {
  if (typeof value === "string" && value.startsWith("blob:")) {
    const response = await fetch(value);
    if (!response.ok) throw new Error("A draft photo could not be saved.");
    const blob = await response.blob();
    const dataURL = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    return dataURL as T;
  }
  if (Array.isArray(value)) return await Promise.all(value.map(durableSnapshot)) as T;
  if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    const entries = await Promise.all(Object.entries(value).map(async ([key, child]) => [key, await durableSnapshot(child)] as const));
    return Object.fromEntries(entries) as T;
  }
  return value;
}

export function useWorkspaceDraft<T>(
  key: string,
  createInitial: () => T,
  accepts: (value: unknown) => value is T
) {
  const initial = useRef(createInitial);
  const validator = useRef(accepts);
  const [value, setValueState] = useState<T>(createInitial);
  const valueRef = useRef(value);
  // Captured once when this editor opens; completion never advances this ref.
  const generationRef = useRef<string | null>(null);
  const pendingSubmissionRef = useRef<string>();
  const revision = useRef(0);
  const persistedRevision = useRef(0);
  const readyRef = useRef(false);
  const mounted = useRef(false);
  const stopped = useRef(false);
  const submittingRef = useRef(false);
  const pendingSaves = useRef(0);
  const [ready, setReady] = useState(false);
  const [storageReady, setStorageReady] = useState(false);
  const [invalidated, setInvalidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [restored, setRestored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedRevision, setSavedRevision] = useState(0);

  const invalidate = useCallback(() => {
    stopped.current = true;
    if (mounted.current) setInvalidated(true);
  }, []);

  const setValue: Dispatch<SetStateAction<T>> = useCallback((update) => {
    if (!readyRef.current || stopped.current || submittingRef.current || pendingSubmissionRef.current || !generationRef.current) return;
    const next = typeof update === "function" ? (update as (current: T) => T)(valueRef.current) : update;
    valueRef.current = next;
    revision.current += 1;
    setValueState(next);
  }, []);

  const saveNow = useCallback(async () => {
    const generation = generationRef.current;
    if (!readyRef.current || stopped.current || submittingRef.current || pendingSubmissionRef.current || !generation) return false;
    const nextRevision = revision.current;
    const snapshot = valueRef.current;
    pendingSaves.current += 1;
    if (mounted.current) setSaving(true);
    try {
      await enqueue(key, () => withDraftLock(key, async () => {
        if (stopped.current) throw new StaleDraftError();
        if (persistedRevision.current >= nextRevision) {
          requireCurrent(await draftOperation(key, "check", generation));
          return;
        }
        const durable = await durableSnapshot(snapshot);
        requireCurrent(await draftOperation(key, "put", generation, durable));
      }));
      persistedRevision.current = Math.max(persistedRevision.current, nextRevision);
      if (mounted.current) {
        setSavedRevision(persistedRevision.current);
        setSaveError(null);
      }
      return true;
    } catch (error) {
      if (error instanceof StaleDraftError) invalidate();
      else if (mounted.current) setSaveError("This draft could not be saved on this device. Keep this window open and try again.");
      return false;
    } finally {
      pendingSaves.current -= 1;
      if (mounted.current) setSaving(pendingSaves.current > 0);
    }
  }, [invalidate, key]);

  useEffect(() => {
    let cancelled = false;
    mounted.current = true;
    stopped.current = false;
    readyRef.current = false;
    generationRef.current = null;
    pendingSubmissionRef.current = undefined;
    revision.current = 0;
    persistedRevision.current = 0;
    setReady(false);
    setStorageReady(false);
    setInvalidated(false);
    setPendingSubmission(false);
    setRestored(false);
    setSaveError(null);
    setSavedRevision(0);
    const fresh = initial.current();
    valueRef.current = fresh;
    setValueState(fresh);
    void enqueue(key, () => withDraftLock(key, () => draftOperation<T>(key, "read")))
      .then((snapshot) => {
        if (cancelled) return;
        const record = snapshot.record;
        const belongsToGeneration = record?.generation === snapshot.generation
          || (!record?.generation && snapshot.generation === INITIAL_GENERATION);
        let legacyCompletion = 0;
        try { legacyCompletion = Number(localStorage.getItem(completionKey(key))) || 0; } catch { /* Durable markers take priority. */ }
        if (record && belongsToGeneration && (record.generation || record.updatedAt > legacyCompletion)) {
          if (record.version !== 1 || !validator.current(record.value)) throw new Error("Unrecognised draft.");
          valueRef.current = record.value;
          setValueState(record.value);
          setRestored(true);
        }
        generationRef.current = snapshot.generation;
        pendingSubmissionRef.current = snapshot.pendingSubmissionID;
        setPendingSubmission(Boolean(snapshot.pendingSubmissionID));
        readyRef.current = true;
        setStorageReady(true);
      })
      .catch(() => {
        if (!cancelled) setSaveError("Draft recovery could not be checked on this device. Close this draft and reopen it before adding an item.");
      })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; mounted.current = false; readyRef.current = false; };
  }, [key]);

  useEffect(() => {
    const receive = (notice: CompletionNotice | undefined) => {
      if (!stopped.current && notice?.key === key && notice.generation === generationRef.current) invalidate();
    };
    const local = (event: Event) => receive((event as CustomEvent<CompletionNotice>).detail);
    const storage = (event: StorageEvent) => {
      const generation = generationRef.current;
      if (generation && event.key === generationKey(key, generation)) receive(completionFor(key, generation));
    };
    let channel: BroadcastChannel | undefined;
    try {
      channel = new BroadcastChannel(DRAFT_CHANNEL);
      channel.onmessage = (event: MessageEvent<CompletionNotice>) => receive(event.data);
    } catch { /* Generation checks still protect tabs without messaging support. */ }
    window.addEventListener(DRAFT_CHANNEL, local);
    window.addEventListener("storage", storage);
    return () => {
      channel?.close();
      window.removeEventListener(DRAFT_CHANNEL, local);
      window.removeEventListener("storage", storage);
    };
  }, [invalidate, key]);

  useEffect(() => {
    if (!ready || stopped.current || revision.current === persistedRevision.current) return;
    const timer = window.setTimeout(() => { void saveNow(); }, 350);
    return () => window.clearTimeout(timer);
  }, [ready, value, saveNow]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!stopped.current && revision.current !== persistedRevision.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const checkpoint = () => { if (document.visibilityState === "hidden") void saveNow(); };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("visibilitychange", checkpoint);
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.removeEventListener("visibilitychange", checkpoint);
    };
  }, [saveNow]);

  const finish = useCallback(async () => {
    const generation = generationRef.current;
    const pendingSubmissionID = pendingSubmissionRef.current;
    if (!readyRef.current || stopped.current || submittingRef.current || !generation) throw new StaleDraftError();
    try {
      return await enqueue(key, () => withDraftLock(key, async () => {
        if (stopped.current) throw new StaleDraftError();
        const cleared = await completeDraft(key, generation, false, pendingSubmissionID);
        stopped.current = true;
        return cleared;
      }, Boolean(pendingSubmissionID)));
    } catch (error) {
      if (error instanceof StaleDraftError) invalidate();
      throw error;
    }
  }, [invalidate, key]);

  const submit = useCallback(async function submit<R>(operation: () => Promise<R>): Promise<{ value: R; cleared: boolean }> {
    const generation = generationRef.current;
    if (!mounted.current || !readyRef.current || stopped.current || submittingRef.current || pendingSubmissionRef.current || !generation) throw new StaleDraftError();
    submittingRef.current = true;
    setSubmitting(true);
    try {
      return await enqueue(key, () => withDraftLock(key, async () => {
        requireCurrent(await draftOperation(key, "check", generation));
        if (!mounted.current || stopped.current) throw new StaleDraftError();
        const submissionID = crypto.randomUUID();
        try {
          const durable = await durableSnapshot(valueRef.current);
          requireCurrent(await draftOperation(key, "claim", generation, durable, undefined, submissionID));
        } catch (error) {
          if (error instanceof StaleDraftError) throw error;
          throw new Error("This draft could not be prepared safely. No item was submitted. Keep it open and try again.");
        }
        pendingSubmissionRef.current = submissionID;
        setPendingSubmission(true);
        const result = await operation();
        stopped.current = true;
        let cleared = false;
        try { cleared = await completeDraft(key, generation, true, submissionID); }
        catch { /* Inventory succeeded; report cleanup uncertainty without offering a duplicate retry. */ }
        return { value: result, cleared };
      }, true));
    } catch (error) {
      if (error instanceof StaleDraftError) invalidate();
      throw error;
    } finally {
      submittingRef.current = false;
      if (mounted.current) setSubmitting(false);
    }
  }, [invalidate, key]);

  const canEdit = ready && storageReady && !invalidated && !stopped.current && !pendingSubmission;
  const canDiscard = ready && storageReady && !invalidated && !stopped.current && !submitting;
  const canSubmit = canEdit && !submitting && lockingAvailable();
  const submitError = canEdit && !lockingAvailable()
    ? "This browser cannot safely add this draft. Use a supported browser before adding the item."
    : null;
  return { value, setValue, ready, restored, saving, saveError, saveNow, finish, submit, submitting, pendingSubmission, invalidated, canEdit, canDiscard, canSubmit, submitError, dirty: revision.current !== savedRevision };
}

export function useDraftField<T, K extends keyof T>(
  draft: { value: T; setValue: Dispatch<SetStateAction<T>> },
  key: K
): [T[K], Dispatch<SetStateAction<T[K]>>] {
  const setValue = draft.setValue;
  const setField: Dispatch<SetStateAction<T[K]>> = useCallback((update) => {
    setValue((current) => ({
      ...current,
      [key]: typeof update === "function" ? (update as (value: T[K]) => T[K])(current[key]) : update
    }));
  }, [key, setValue]);
  return [draft.value[key], setField];
}
